<template>
  <AdminLayout>
    <Head title="Company Integrations" />

    <template #pageTitle> Integrations </template>

    <template #headingOptions>
      <button
        @click="showingCreatePrompt = true"
        type="button"
        class="inline-flex items-center px-4 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
      >
        <PlusSmIconSolid
          class="-ml-0.5 mr-2 h-4 w-4"
          aria-hidden="true"
        />
        Add Integration
      </button>
    </template>

    <div class="flex-1 bg-gray-100">
      <data-table
        :items="integrations.data"
        :headers="headers"
        :actions="[]"
        :selectable="false"
      >
        <template v-slot:rows="slotRow">
          <index-row
            v-slot:rows="slotRow"
            v-bind="slotRow"
            :show-route="showIntegrationRoute"
          />
        </template>
      </data-table>
    </div>

    <template #modals>
      <SmallModal
        :is-open="showingCreatePrompt"
        @close="showingCreatePrompt = false"
      >
        <template #header> New Integration </template>

        <div class="mt-4">
          <div class="input-group">
            <label for="name"> Integration </label>
            <select
              id="name"
              v-model="createForm.name"
              name="name"
            >
              <option value="">Select integration</option>
              <option
                v-for="(display, key) in integrationNames"
                :value="key"
              >
                {{ display }}
              </option>
            </select>
            <p
              v-if="getErrors('name', createForm)"
              class="input-error"
            >
              {{ getErrors('name', createForm) }}
            </p>
          </div>
        </div>

        <template #footer>
          <button
            type="button"
            class="btn btn-primary"
            @click="createIntegration()"
          >
            Add
          </button>
          <button
            type="button"
            class="btn btn-secondary ml-3"
            @click="showingCreatePrompt = false"
          >
            Cancel
          </button>
        </template>
      </SmallModal>

      <SmallModal
        :is-open="showingDeletePrompt"
        @close="showingDeletePrompt = false"
      >
        <template #header> Deleting Integration </template>

        <div class="mt-4">
          <div class="input-group">
            <label> You are about to delete the following integration: </label>
            <p class="mt-2 font-bold">
              {{ integrationToDelete.display_name }}
            </p>
          </div>
        </div>

        <template #footer>
          <button
            type="button"
            class="btn btn-danger"
            @click="deleteIntegration()"
          >
            Delete
          </button>
          <button
            type="button"
            class="btn btn-secondary ml-3"
            @click="showingDeletePrompt = false"
          >
            Cancel
          </button>
        </template>
      </SmallModal>
    </template>
  </AdminLayout>
</template>

<script>
import { Head, Link, useForm } from '@inertiajs/vue3'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { DotsHorizontalIcon } from '@heroicons/vue/solid'
import AdminLayout from '@/Layouts/AdminLayout.vue'
import Pagination from '@/Components/Pagination.vue'
import SmallModal from '@/Components/Modals/SmallModal.vue'
import dateFilters from '@/Mixins/dateFilters'
import isUrlAware from '@/Mixins/isUrlAware'
import checksErrors from '@/Mixins/checksErrors'
import interactsWithCurrentLocation from '@/Mixins/interactsWithCurrentLocation'
import { PlusSmIcon as PlusSmIconSolid } from '@heroicons/vue/solid'
import { PlusSmIcon as PlusSmIconOutline } from '@heroicons/vue/outline'
import DataTable from '@/Components/Table/DataTable'
import IndexRow from '@/Pages/Company/Integrations/Components/IndexRow'

export default {
  mixins: [dateFilters, isUrlAware, checksErrors, interactsWithCurrentLocation],

  components: {
    AdminLayout,
    Head,
    Link,
    Pagination,
    DotsHorizontalIcon,
    Popover,
    PopoverButton,
    PopoverPanel,
    SmallModal,
    PlusSmIconOutline,
    PlusSmIconSolid,
    DataTable,
    IndexRow
  },

  props: {
    integrations: Array
  },

  data() {
    return {
      showingCreatePrompt: false,
      showingDeletePrompt: false,
      integrationToDelete: null
    }
  },

  setup() {
    const createForm = useForm({
      name: ''
    })

    const deleteForm = useForm({})

    return {
      createForm,
      deleteForm
    }
  },

  computed: {
    headers() {
      return ['Name', 'Type', 'Location(s)', 'Creation Date', 'Status']
    },
    showIntegrationRoute() {
      // A better argument name here would be 'package' but thats a reserved word in javascript
      return (item) => {
        return route('company.integrations.edit', { integration: item.id })
      }
    },
    integrationNames() {
      return this.$page.props.util.integration_names
    }
  },

  methods: {
    createIntegration() {
      this.createForm.post(route('company.integrations.store'), {
        onSuccess: () => {
          this.createForm.reset('name')
        }
      })
    },
    openDeletePrompt(integration) {
      this.integrationToDelete = integration
      this.showingDeletePrompt = true
    },
    deleteIntegration() {
      if (!this.integrationToDelete) {
        return
      }

      this.deleteForm.delete(route('company.integrations.destroy', { integration: this.integrationToDelete.id }), {
        onSuccess: () => {}
      })
    }
  }
}
</script>
